<template>
    <div>
        <!-- Optional section header: big title on the left, muted subtitle on the
             right, with an underline. Shown when `title` or `subtitle` is set. -->
        <div v-if="title || subtitle"
            class="flex items-end justify-between gap-4 pb-3 mb-6 border-b-2 border-gray-900/80">
            <h2 v-if="title" class="m-0 text-2xl font-bold tracking-tight text-gray-900 md:text-3xl"
                style="font-family: Georgia, 'Times New Roman', serif;">
                {{ title }}
            </h2>
            <span v-if="subtitle" class="text-sm text-right text-gray-500 whitespace-nowrap">
                {{ subtitle }}
            </span>
        </div>

        <!-- Filter bar (tablet/desktop): search, category/destination/activity
             dropdowns and sort. Hidden on phones, where the controls move into a
             bottom drawer (below) to save vertical space.
               • sm (tablets): 2-column grid, search spans the full top row
               • lg (desktop): a single inline row — search grows wider, the
                 dropdowns share the remaining width evenly (flex-1)
             Opt-in via the `filters` prop. -->
        <div v-if="showFilters"
            class="hidden gap-3 mb-6 sm:grid sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center">
            <div v-if="filter_search"
                class="relative w-full sm:col-span-2 lg:w-auto lg:flex-[2] lg:min-w-[13rem] lg:max-w-sm">
                <span class="absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 pi pi-search text-gray-400"
                    style="font-size: 14px" />
                <input-text v-model="searchTerm" type="text" placeholder="Search posts..." fluid class="!pl-9 w-full"
                    @keyup.enter="applyFilters" />
                <button v-if="searchTerm" type="button"
                    class="absolute -translate-y-1/2 right-3 top-1/2 pi pi-times text-gray-400 hover:text-gray-600"
                    style="font-size: 12px" aria-label="Clear search" @click="clearSearch" />
            </div>

            <p-select v-if="filter_categories" v-model="selectedCategory" :options="categoryOptions"
                option-label="label" option-value="value" placeholder="All categories" show-clear
                class="w-full text-sm lg:flex-1 lg:min-w-[10rem]" />

            <p-select v-if="filter_locations" v-model="selectedLocation" :options="locationOptions"
                option-label="label" option-value="value" placeholder="All destinations" show-clear
                class="w-full text-sm lg:flex-1 lg:min-w-[10rem]" />

            <p-select v-if="filter_activities" v-model="selectedActivity" :options="activityOptions"
                option-label="label" option-value="value" placeholder="All activities" show-clear
                class="w-full text-sm lg:flex-1 lg:min-w-[10rem]" />

            <p-select v-if="filter_sort" v-model="sortOrder" :options="sortOptions" option-label="label"
                option-value="value" class="w-full text-sm lg:flex-1 lg:min-w-[9rem] lg:max-w-[12rem]" />
        </div>

        <!-- Filter trigger (phones only): opens the bottom drawer. Shows a count
             badge when any filter is active. -->
        <div v-if="showFilters" class="mb-6 sm:hidden">
            <p-button type="button" outlined severity="contrast" fluid icon="pi pi-sliders-h"
                :label="activeFilterCount ? `${filter_label} (${activeFilterCount})` : filter_label"
                @click="visibleFilter = true" />
        </div>

        <!-- Mobile filter drawer: the same controls stacked full-width, slides down
             from the top. Filters auto-apply via watchers, so the footer button
             just dismisses the drawer. -->
        <drawer v-if="showFilters" position="top" v-model:visible="visibleFilter" :header="filter_title"
            style="height: fit-content">
            <div class="flex flex-col gap-3">
                <div v-if="filter_search" class="relative w-full">
                    <span class="absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 pi pi-search text-gray-400"
                        style="font-size: 14px" />
                    <input-text v-model="searchTerm" type="text" placeholder="Search posts..." fluid class="!pl-9 w-full"
                        @keyup.enter="applyFilters" />
                    <button v-if="searchTerm" type="button"
                        class="absolute -translate-y-1/2 right-3 top-1/2 pi pi-times text-gray-400 hover:text-gray-600"
                        style="font-size: 12px" aria-label="Clear search" @click="clearSearch" />
                </div>

                <p-select v-if="filter_categories" v-model="selectedCategory" :options="categoryOptions"
                    option-label="label" option-value="value" placeholder="All categories" show-clear
                    class="w-full text-sm" />

                <p-select v-if="filter_locations" v-model="selectedLocation" :options="locationOptions"
                    option-label="label" option-value="value" placeholder="All destinations" show-clear
                    class="w-full text-sm" />

                <p-select v-if="filter_activities" v-model="selectedActivity" :options="activityOptions"
                    option-label="label" option-value="value" placeholder="All activities" show-clear
                    class="w-full text-sm" />

                <p-select v-if="filter_sort" v-model="sortOrder" :options="sortOptions" option-label="label"
                    option-value="value" class="w-full text-sm" />

                <p-button type="button" fluid label="Show results" @click="visibleFilter = false" />
            </div>
        </drawer>

        <!-- Skeleton placeholders while (re)fetching -->
        <div v-if="loading">
            <div v-if="layout === 'grid' || layout === 'mosaic'" :class="gridClasses">
                <div v-for="n in skeletonCount" :key="n" class="my-3">
                    <post-item loading />
                </div>
            </div>
            <scroll-slider v-else>
                <div v-for="n in skeletonCount" :key="n" class="min-w-[280px] max-w-[320px] px-2">
                    <post-item loading />
                </div>
            </scroll-slider>
        </div>

        <div v-else-if="posts.length > 0">
            <!-- Grid Layout -->
            <div v-if="layout === 'grid'" :class="gridClasses">
                <div v-for="(post, idx) in posts" :key="post.id" class="my-3">
                    <post-item :post="post" :cache="shouldCache" />
                </div>
            </div>

            <!-- Mosaic Layout: a hero post beside a stacked column of the rest. -->
            <post-mosaic v-else-if="layout === 'mosaic'" :posts="posts" />

            <!-- Carousel Layout -->
            <div v-else-if="layout === 'carousel'">
                <carousel v-if="posts.length >= 3" circular :value="posts" :num-visible="3" :num-scroll="1"
                    :responsive-options="carouselResponsiveOptions">
                    <template #item="{ data: post }">
                        <div class="p-3">
                            <post-item :post="post" :cache="shouldCache" />
                        </div>
                    </template>
                </carousel>

                <scroll-slider v-else>
                    <div v-for="post in posts" :key="post.id" class="min-w-[280px] max-w-[320px] px-2">
                        <post-item :post="post" :cache="shouldCache" />
                    </div>
                </scroll-slider>
            </div>

            <!-- Scroll Layout -->
            <div v-else-if="layout === 'scroll'">
                <scroll-slider>
                    <div v-for="post in posts" :key="post.id" class="min-w-[280px] max-w-[320px] px-2">
                        <post-item :post="post" :cache="shouldCache" />
                    </div>
                </scroll-slider>
            </div>

            <!-- Pagination -->
            <div v-if="paginate && pagination && pagination.totalPages > 1"
                class="flex items-center justify-center w-full mt-6">
                <div v-if="pagination.totalPages > pagination.currentPage"
                    class="flex flex-col items-center justify-center w-full md:w-1/2">
                    <div class="text-sm font-thin">
                        {{ `Showing ${pagination.count} of ${pagination.total}` }}
                    </div>
                    <p-button fluid
                        :label="`Load ${Math.min(pagination.perPage, pagination.total - pagination.count)} more posts`"
                        @click="loadMore" :loading="loadingMore" :disabled="loadingMore" />
                </div>
            </div>
        </div>

        <div v-else class="flex items-center justify-center w-full p-10 text-gray-500">
            No posts found
        </div>
    </div>
</template>

<script lang="ts" setup>
import { useApiProxy } from '@/Hook/useProxy';
import { Post, Pagination, PostCategory } from '@/types';
import { computed, onMounted, ref, watch } from 'vue';
import { Carousel, Button as PButton, Select as PSelect, InputText, Drawer } from 'primevue';
import PostItem from './PostItem.vue';
import PostMosaic from './PostMosaic.vue';
import ScrollSlider from '@/Components/ScrollSlider.vue';

export interface GridConfig {
    cols?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    '2xl'?: number;
    gap?: number;
}

const props = withDefaults(defineProps<{
    // Data source options
    posts?: Post[];
    slugs?: string[] | string;

    // Category filtering
    category?: string | number;
    categories?: (string | number)[] | string;

    // Location filtering
    location?: string | number;
    locations?: (string | number)[] | string;

    // Activity filtering (Tour Categories tags)
    activity?: string | number;
    activities?: (string | number)[] | string;

    // Interactive filter UI. `filters` toggles the whole bar; the granular
    // flags let an author hide individual controls (e.g. filters + filter_sort=false).
    filters?: boolean;
    filter_search?: boolean;
    filter_categories?: boolean;
    filter_locations?: boolean;
    filter_activities?: boolean;
    filter_sort?: boolean;

    // Labels for the mobile filter UI. `filter_label` is the trigger button
    // text; `filter_title` is the bottom drawer's header.
    filter_label?: string;
    filter_title?: string;

    // Default sort direction on publish date ('desc' = newest first).
    order_dir?: 'asc' | 'desc';

    // Optional section header. `title` renders a large heading; `subtitle`
    // renders muted text on the right of the same row (e.g. a source label).
    title?: string;
    subtitle?: string;

    // Layout configuration. `mosaic` renders a hero post beside a stacked
    // column of the remaining posts (pair with a small `limit`, e.g. 3).
    layout?: 'grid' | 'carousel' | 'scroll' | 'mosaic';
    grid?: GridConfig;

    // Pagination
    paginate?: boolean;
    per_page?: number;

    // Caching
    cache?: boolean;

    // Limit results (when not paginating)
    limit?: number;


    data?: Record<string, any>;

    // Server-resolved payload, injected by ShortcodeResolver for the
    // `[posts ...]` shortcode so the listing renders during SSR.
    ssrData?: { posts?: Post[]; pagination?: Pagination; meta?: any };
}>(), {
    layout: 'grid',
    per_page: 12,
    filters: false,
    filter_search: true,
    filter_categories: true,
    filter_locations: true,
    filter_activities: true,
    filter_sort: true,
    filter_label: 'Filters',
    filter_title: 'Filter posts',
    order_dir: 'desc',
    grid: () => ({ cols: 1, sm: 2, md: 2, lg: 3, xl: 4, gap: 4 })
});

// Default cache to false when in Live.Render context, otherwise true
const isLiveRender = typeof window !== 'undefined' && window.location.href.includes('Live.Render');
const shouldCache = computed(() => props.cache ?? !isLiveRender);

// Seed from server-resolved data (or a direct `posts` prop) synchronously so
// the listing renders during SSR; the mount fetch only runs when neither exists.
const posts = ref<Post[]>(props.posts ?? props.ssrData?.posts ?? []);
const pagination = ref<Pagination | undefined>(props.ssrData?.pagination);
const loadingMore = ref(false);

// Interactive filter state.
const showFilters = computed(() => props.filters);
const selectedCategory = ref<string | number | null>(props.category ?? null);
const selectedLocation = ref<string | number | null>(props.location ?? null);
const selectedActivity = ref<string | number | null>(props.activity ?? null);
const searchTerm = ref<string>('');
const sortOrder = ref<'asc' | 'desc'>(props.order_dir ?? 'desc');

// Mobile filter drawer visibility.
const visibleFilter = ref(false);

// Number of active filters, shown as a badge on the mobile trigger. Sort is
// excluded — it always has a value and isn't really a "filter".
const activeFilterCount = computed(() =>
    [selectedCategory.value, selectedLocation.value, selectedActivity.value, searchTerm.value.trim()]
        .filter(Boolean).length
);

const sortOptions = [
    { label: 'Newest first', value: 'desc' },
    { label: 'Oldest first', value: 'asc' },
];

// Categories available for the dropdown, pulled from the listing response meta
// (admin2 nests them under `meta.meta.categories`).
const extractCategories = (meta: any): PostCategory[] =>
    meta?.categories ?? meta?.meta?.categories ?? [];

const availableCategories = ref<PostCategory[]>(extractCategories(props.ssrData?.meta));

const categoryOptions = computed(() =>
    availableCategories.value.map((c) => ({ label: c.name, value: c.slug }))
);

// Locations available for the dropdown, also carried in the listing response meta.
const extractLocations = (meta: any): { name: string; slug: string }[] =>
    meta?.locations ?? meta?.meta?.locations ?? [];

const availableLocations = ref<{ name: string; slug: string }[]>(extractLocations(props.ssrData?.meta));

const locationOptions = computed(() =>
    availableLocations.value.map((l) => ({ label: l.name, value: l.slug }))
);

// Activities (Tour Categories tags) available for the dropdown, carried in meta.
const extractActivities = (meta: any): { name: string; slug: string }[] =>
    meta?.activities ?? meta?.meta?.activities ?? [];

const availableActivities = ref<{ name: string; slug: string }[]>(extractActivities(props.ssrData?.meta));

const activityOptions = computed(() =>
    availableActivities.value.map((a) => ({ label: a.name, value: a.slug }))
);

const { loading, request } = useApiProxy<{ posts: Post[]; pagination?: Pagination; meta?: any }>();
const { request: paginationRequest } = useApiProxy<{ posts: Post[]; pagination?: Pagination; meta?: any }>();

// Compute grid classes based on configuration
const gridClasses = computed(() => {
    const g = props.grid;
    const classes = ['grid'];

    // Base columns
    classes.push(`grid-cols-${g.cols ?? 1}`);

    // Responsive columns
    if (g.sm) classes.push(`sm:grid-cols-${g.sm}`);
    if (g.md) classes.push(`md:grid-cols-${g.md}`);
    if (g.lg) classes.push(`lg:grid-cols-${g.lg}`);
    if (g.xl) classes.push(`xl:grid-cols-${g.xl}`);
    if (g['2xl']) classes.push(`2xl:grid-cols-${g['2xl']}`);

    // Gap
    classes.push(`gap-${g.gap ?? 4}`);

    return classes.join(' ');
});

// How many skeleton cards to show while fetching — mirror the eventual count.
const skeletonCount = computed(() => {
    if (props.paginate) return props.per_page ?? 12;
    return Math.min(props.limit ?? props.per_page ?? 8, 12);
});

// Carousel responsive options
const carouselResponsiveOptions = [
    { breakpoint: '1400px', numVisible: 3, numScroll: 1 },
    { breakpoint: '1024px', numVisible: 2, numScroll: 1 },
    { breakpoint: '768px', numVisible: 1, numScroll: 1 },
];

// Helper to normalize array or comma-separated string to comma-separated string
const toCommaSeparated = (value: string | (string | number)[] | undefined): string | undefined => {
    if (!value) return undefined;
    if (Array.isArray(value)) return value.join(',');
    return value; // Already a comma-separated string
};

// Build request URL with filters
const buildRequestUrl = (additionalParams: Record<string, any> = {}) => {
    const params: Record<string, any> = {
        ...additionalParams
    };

    // Category filter: an interactive selection wins over the authored props.
    if (selectedCategory.value) {
        params.category = selectedCategory.value;
    } else if (props.category) {
        params.category = props.category;
    } else if (props.categories) {
        const categories = toCommaSeparated(props.categories);
        if (categories) params.categories = categories;
    }

    // Location filter: an interactive selection wins over the authored props.
    if (selectedLocation.value) {
        params.location = selectedLocation.value;
    } else if (props.location) {
        params.location = props.location;
    } else if (props.locations) {
        const locations = toCommaSeparated(props.locations);
        if (locations) params.locations = locations;
    }

    // Activity filter: an interactive selection wins over the authored props.
    if (selectedActivity.value) {
        params.activity = selectedActivity.value;
    } else if (props.activity) {
        params.activity = props.activity;
    } else if (props.activities) {
        const activities = toCommaSeparated(props.activities);
        if (activities) params.activities = activities;
    }

    // Free-text search
    const search = searchTerm.value.trim();
    if (search) params.search = search;

    // Sort by publish date
    if (sortOrder.value) params.order_dir = sortOrder.value;

    // Add specific post slugs
    if (props.slugs) {
        const slugs = toCommaSeparated(props.slugs);
        if (slugs) params.slugs = slugs;
    }

    // Add pagination params
    if (props.paginate) {
        params.paginate = true;
        params.per_page = props.per_page ?? 12;
    } else if (props.limit) {
        params.limit = props.limit;
    }

    return route('get.posts', { ...params, ...(props.data ?? {}) });
};

// Fetch posts. `force` bypasses the SSR/direct-posts short-circuit so filter
// changes always hit the API.
const fetchPosts = async (force = false) => {
    if (!force) {
        // If posts are provided directly, use them
        if (props.posts?.length) {
            posts.value = props.posts;
            return;
        }

        // Server already resolved this shortcode — reuse it, don't re-fetch.
        if (props.ssrData?.posts) {
            posts.value = props.ssrData.posts;
            if (props.ssrData.pagination) pagination.value = props.ssrData.pagination;
            return;
        }
    }

    // Don't cache active searches — they'd bloat localStorage with one-off keys.
    const useCache = shouldCache.value && !searchTerm.value.trim();

    const response = await request(buildRequestUrl(), {}, useCache, undefined, 'posts_listing');

    if (response) {
        posts.value = response.posts ?? [];
        pagination.value = response.pagination;
        const cats = extractCategories(response.meta);
        if (cats.length) availableCategories.value = cats;
        const locs = extractLocations(response.meta);
        if (locs.length) availableLocations.value = locs;
        const acts = extractActivities(response.meta);
        if (acts.length) availableActivities.value = acts;
    }
};

// Load more posts (pagination)
const loadMore = async () => {
    if (!pagination.value || loadingMore.value) return;

    loadingMore.value = true;
    const nextPage = pagination.value.currentPage + 1;

    const response = await paginationRequest(
        buildRequestUrl({ page: nextPage }),
        {},
        false // Don't cache pagination requests
    );

    if (response?.posts) {
        posts.value = [...posts.value, ...response.posts];

        if (response.pagination) {
            pagination.value = {
                ...response.pagination,
                count: posts.value.length
            };
        }
    }

    loadingMore.value = false;
};

// Re-run the query from page one whenever an interactive filter changes.
const applyFilters = () => fetchPosts(true);

const clearSearch = () => {
    searchTerm.value = '';
};

// Debounce free-text search so we don't fire a request per keystroke.
let searchDebounce: ReturnType<typeof setTimeout> | undefined;
watch(searchTerm, () => {
    if (searchDebounce) clearTimeout(searchDebounce);
    searchDebounce = setTimeout(() => applyFilters(), 350);
});

// Category / location / activity / sort changes refetch immediately.
watch([selectedCategory, selectedLocation, selectedActivity, sortOrder], () => applyFilters());

// Watch for authored prop changes to refetch
watch(
    () => [props.category, props.categories, props.location, props.locations, props.activity, props.activities, props.slugs],
    () => fetchPosts(),
    { deep: true }
);

onMounted(() => {
    fetchPosts();
});
</script>
